import { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { 
  Layers, SidebarClose, Eye, EyeOff, Save, RefreshCw,
  Settings2, Users, Check, ExternalLink, Image, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const TIERS = [
  { value: 'free', label: 'Free Users' },
  { value: 'founding_member', label: 'Founding Members' },
  { value: 'premium', label: 'Premium Users' },
];

const DashboardDisplaySettings = () => {
  const [settings, setSettings] = useState({
    carousel: {
      enabled: true,
      visible_tiers: ['free', 'founding_member', 'premium'],
    },
    sidebar_banner: {
      enabled: true,
      visible_tiers: ['free'],
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Preview state
  const [showCarouselPreview, setShowCarouselPreview] = useState(false);
  const [showSidebarPreview, setShowSidebarPreview] = useState(false);
  const [banners, setBanners] = useState([]);
  const [promoCampaigns, setPromoCampaigns] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [activeBannerCount, setActiveBannerCount] = useState(0);
  const [activePromoCount, setActivePromoCount] = useState(0);

  useEffect(() => {
    fetchSettings();
    fetchBannerCount();
    fetchPromoCount();
  }, []);
  
  // Fetch banners for preview
  const fetchBanners = async () => {
    setLoadingBanners(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/banners`, {
        withCredentials: true
      });
      setBanners(res.data.banners || []);
      setActiveBannerCount((res.data.banners || []).filter(b => b.is_active).length);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
      toast.error('Failed to load banners for preview');
    } finally {
      setLoadingBanners(false);
    }
  };
  
  // Quick fetch just to get active count
  const fetchBannerCount = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/banners`, {
        withCredentials: true
      });
      setActiveBannerCount((res.data.banners || []).filter(b => b.is_active).length);
    } catch (error) {
      console.error('Failed to fetch banner count:', error);
    }
  };
  
  // Fetch promo campaigns count for the carousel
  const fetchPromoCount = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/promos`, {
        withCredentials: true
      });
      setActivePromoCount((res.data.campaigns || []).filter(c => c.is_active).length);
      setPromoCampaigns((res.data.campaigns || []).filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch promo count:', error);
    }
  };
  
  const handleOpenPreview = (type) => {
    if (banners.length === 0) {
      fetchBanners();
    }
    if (type === 'carousel') {
      setShowCarouselPreview(true);
    } else {
      setShowSidebarPreview(true);
    }
  };

  const fetchSettings = async () => {
    try {
      
      const res = await axios.get(`${API_BASE}/api/admin/settings/dashboard-display`, {
        withCredentials: true
      });
      if (res.data.settings) {
        // Only load carousel and sidebar settings
        setSettings(prev => ({
          carousel: res.data.settings.carousel || prev.carousel,
          sidebar_banner: res.data.settings.sidebar_banner || prev.sidebar_banner,
        }));
      }
    } catch (error) {
      console.error('Failed to load dashboard display settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      
      // Preserve video_embed settings from server, only update carousel and sidebar
      const currentRes = await axios.get(`${API_BASE}/api/admin/settings/dashboard-display`, {
        withCredentials: true
      });
      const existingSettings = currentRes.data.settings || {};
      
      await axios.put(`${API_BASE}/api/admin/settings/dashboard-display`, 
        { 
          settings: {
            ...existingSettings,
            carousel: settings.carousel,
            sidebar_banner: settings.sidebar_banner,
          }
        },
        { withCredentials: true }
      );
      toast.success('Display settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleTier = (section, tier) => {
    setSettings(prev => {
      const currentTiers = prev[section].visible_tiers || [];
      const newTiers = currentTiers.includes(tier)
        ? currentTiers.filter(t => t !== tier)
        : [...currentTiers, tier];
      return {
        ...prev,
        [section]: {
          ...prev[section],
          visible_tiers: newTiers,
        }
      };
    });
  };
  
  // Render tier selector (avoiding nested component definition)
  const renderTierSelector = (section, label) => (
    <div className="mt-3">
      <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
        <Users className="w-4 h-4" />
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {TIERS.map(tier => {
          const isSelected = settings[section].visible_tiers?.includes(tier.value);
          return (
            <button
              key={tier.value}
              onClick={() => toggleTier(section, tier.value)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1.5 ${
                isSelected 
                  ? 'bg-[#10B981]/20 border border-[#10B981] text-[#10B981]' 
                  : 'bg-gray-800 border border-gray-700 text-gray-400 hover:border-gray-600'
              }`}
            >
              {isSelected && <Check className="w-3 h-3" />}
              {tier.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings2 className="w-6 h-6 text-[#10B981]" />
          <div>
            <h2 className="text-xl font-bold text-white">Display Toggles</h2>
            <p className="text-sm text-gray-400">Toggle carousel and sidebar visibility per tier</p>
          </div>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-gradient-to-r from-[#10B981] to-[#059669]"
        >
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      {/* Promo Carousel Section (formerly "Banner Carousel") */}
      <div 
        className="p-5 rounded-xl border"
        style={{
          background: settings.carousel.enabled ? 'rgba(16, 185, 129, 0.05)' : 'rgba(30, 30, 30, 0.5)',
          borderColor: settings.carousel.enabled ? 'rgba(16, 185, 129, 0.3)' : 'rgba(75, 75, 75, 0.5)',
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${settings.carousel.enabled ? 'bg-[#10B981]/20' : 'bg-gray-800'}`}>
              <Layers className={`w-5 h-5 ${settings.carousel.enabled ? 'text-[#10B981]' : 'text-gray-500'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-white">Promo Carousel</h3>
              <p className="text-sm text-gray-400">Rotating promos on the main journal dashboard</p>
            </div>
          </div>
          <button
            onClick={() => setSettings(prev => ({
              ...prev,
              carousel: { ...prev.carousel, enabled: !prev.carousel.enabled }
            }))}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              settings.carousel.enabled
                ? 'bg-[#10B981] text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {settings.carousel.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {settings.carousel.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        
        {/* Size & Location Info */}
        <div className="mt-3 p-3 bg-black/20 rounded-lg border border-gray-700/50">
          <div className="flex items-start gap-2">
            <Image className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div className="text-xs text-gray-400">
              <p><strong className="text-gray-300">Location:</strong> Main journal dashboard (top section)</p>
              <p className="mt-1"><strong className="text-gray-300">Content source:</strong> Marketing → Promos (promotional campaigns)</p>
              <p className="mt-1"><strong className="text-gray-300">Note:</strong> This displays Promo Campaigns, NOT banners from Banner Management</p>
            </div>
          </div>
        </div>

        {settings.carousel.enabled && renderTierSelector("carousel", "Visible to:")}
        
        {/* Warning if enabled but no active promo campaigns */}
        {settings.carousel.enabled && activePromoCount === 0 && (
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <EyeOff className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-400">
                <p><strong>No active promo campaigns.</strong> The carousel is enabled but won&apos;t display anything until you create active promos in <strong>Marketing → Promos</strong>.</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Status indicator */}
        {settings.carousel.enabled && (
          <div className="mt-4 pt-4 border-t border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              {activePromoCount > 0 ? (
                <span className="text-[#10B981] flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {activePromoCount} active promo{activePromoCount !== 1 ? 's' : ''} will display
                </span>
              ) : (
                <span className="text-amber-500 flex items-center gap-1">
                  <EyeOff className="w-4 h-4" />
                  No promos to display
                </span>
              )}
            </div>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); /* Navigate to promos tab */ }}
              className="text-sm text-[#df1283] hover:text-[#df1283]/80 flex items-center gap-1"
            >
              Go to Promos →
            </a>
          </div>
        )}
      </div>

      {/* Sidebar Banner Section */}
      <div 
        className="p-5 rounded-xl border"
        style={{
          background: settings.sidebar_banner.enabled ? 'rgba(251, 191, 36, 0.05)' : 'rgba(30, 30, 30, 0.5)',
          borderColor: settings.sidebar_banner.enabled ? 'rgba(251, 191, 36, 0.3)' : 'rgba(75, 75, 75, 0.5)',
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${settings.sidebar_banner.enabled ? 'bg-amber-500/20' : 'bg-gray-800'}`}>
              <SidebarClose className={`w-5 h-5 ${settings.sidebar_banner.enabled ? 'text-amber-500' : 'text-gray-500'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-white">Sidebar Banner</h3>
              <p className="text-sm text-gray-400">Promotional banner below the &quot;Motivate Me&quot; button</p>
            </div>
          </div>
          <button
            onClick={() => setSettings(prev => ({
              ...prev,
              sidebar_banner: { ...prev.sidebar_banner, enabled: !prev.sidebar_banner.enabled }
            }))}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              settings.sidebar_banner.enabled
                ? 'bg-[#10B981] text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {settings.sidebar_banner.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {settings.sidebar_banner.enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
        
        {/* Size & Location Info */}
        <div className="mt-3 p-3 bg-black/20 rounded-lg border border-gray-700/50">
          <div className="flex items-start gap-2">
            <Image className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-gray-400">
              <p><strong className="text-gray-300">Location:</strong> Journal sidebar, below &quot;Motivate Me&quot; button</p>
              <p className="mt-1"><strong className="text-gray-300">Recommended size:</strong> 2500 × 2500px (square)</p>
              <p className="mt-1"><strong className="text-gray-300">Note:</strong> Shows the first active banner from your list</p>
            </div>
          </div>
        </div>

        {settings.sidebar_banner.enabled && renderTierSelector("sidebar_banner", "Visible to:")}
        
        {/* Warning if enabled but no active banners */}
        {settings.sidebar_banner.enabled && activeBannerCount === 0 && (
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <EyeOff className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-400">
                <p><strong>No active banners found.</strong> The sidebar banner is enabled but won&apos;t display anything until you create active banners in Banner Management.</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Preview Button for Sidebar */}
        {settings.sidebar_banner.enabled && (
          <div className="mt-4 pt-4 border-t border-gray-700/50">
            <button
              onClick={() => handleOpenPreview('sidebar')}
              className="flex items-center gap-2 text-sm text-amber-500 hover:text-amber-400 transition-colors"
              data-testid="preview-sidebar-btn"
            >
              <Eye className="w-4 h-4" />
              Preview Sidebar Banner
              {activeBannerCount > 0 && <span className="text-gray-500">({activeBannerCount} active)</span>}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Tip */}
      <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
        <p className="text-sm text-gray-400">
          <strong className="text-gray-300">Tip:</strong> Videos are managed in the Video Embeds section above. 
          Founding Members always see their special badge in the carousel when enabled.
        </p>
      </div>
      
      {/* Info about Banner Management link */}
      <div className="p-4 rounded-lg bg-[#df1283]/10 border border-[#df1283]/30">
        <div className="flex items-start gap-3">
          <Image className="w-5 h-5 text-[#df1283] mt-0.5" />
          <div>
            <p className="text-sm text-white font-medium">Looking to create or edit banners?</p>
            <p className="text-sm text-gray-400 mt-1">
              These settings control <strong>who sees</strong> banners. To create or edit actual banner images and links, 
              go to <strong>Dashboard → Banner Management</strong> section (scroll down on the Overview tab).
            </p>
          </div>
        </div>
      </div>
      
      {/* Banner Carousel Preview Modal */}
      {showCarouselPreview && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
          onClick={() => setShowCarouselPreview(false)}
          data-testid="carousel-preview-modal"
        >
          <div 
            className="bg-gradient-to-br from-[#1a0527] to-[#0f0317] rounded-2xl border border-[#4A0A66] w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Layers className="w-5 h-5 text-[#10B981]" />
                Banner Carousel Preview
              </h3>
              <button
                onClick={() => setShowCarouselPreview(false)}
                className="p-2 rounded-lg hover:bg-[#4A0A66]/50 text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              {loadingBanners ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-[#df1283]" />
                  <p className="text-gray-400">Loading banners...</p>
                </div>
              ) : banners.filter(b => b.is_active).length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No active banners found</p>
                  <p className="text-sm text-gray-500 mt-2">Create banners in the Banner Management section</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-400 mb-4">
                    Showing <strong className="text-white">{banners.filter(b => b.is_active).length}</strong> active banner(s) that rotate in the carousel.
                    <br />
                    <span className="text-[#10B981]">Visible to: {settings.carousel.visible_tiers.map(t => TIERS.find(tier => tier.value === t)?.label).join(', ')}</span>
                    <br />
                    <span className="text-gray-500 text-xs">Recommended size: 1200 × 400px (3:1 ratio)</span>
                  </p>
                  
                  {/* Banner Preview - using 3:1 aspect ratio */}
                  <div className="relative rounded-xl overflow-hidden bg-black/20 border border-[#4A0A66]" style={{ aspectRatio: '3/1' }}>
                    {banners.filter(b => b.is_active).map((banner, idx) => (
                      <div 
                        key={banner.id || idx} 
                        className={`absolute inset-0 ${idx === previewIndex ? 'block' : 'hidden'}`}
                      >
                        {banner.image_url ? (
                          <img 
                            src={banner.image_url} 
                            alt={banner.title || 'Banner'} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
                            <span className="text-white font-bold text-xl">{banner.title || 'Banner'}</span>
                          </div>
                        )}
                        {banner.title && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-3">
                            <p className="text-white font-medium">{banner.title}</p>
                            {banner.subtitle && <p className="text-gray-300 text-sm">{banner.subtitle}</p>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Navigation dots */}
                  {banners.filter(b => b.is_active).length > 1 && (
                    <div className="flex justify-center gap-2 mt-4">
                      {banners.filter(b => b.is_active).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setPreviewIndex(idx)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            idx === previewIndex ? 'bg-[#df1283]' : 'bg-gray-600 hover:bg-gray-500'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Sidebar Banner Preview Modal */}
      {showSidebarPreview && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
          onClick={() => setShowSidebarPreview(false)}
          data-testid="sidebar-preview-modal"
        >
          <div 
            className="bg-gradient-to-br from-[#1a0527] to-[#0f0317] rounded-2xl border border-[#4A0A66] w-full max-w-md max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <SidebarClose className="w-5 h-5 text-amber-500" />
                Sidebar Banner Preview
              </h3>
              <button
                onClick={() => setShowSidebarPreview(false)}
                className="p-2 rounded-lg hover:bg-[#4A0A66]/50 text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              {loadingBanners ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-amber-500" />
                  <p className="text-gray-400">Loading banners...</p>
                </div>
              ) : banners.filter(b => b.is_active).length === 0 ? (
                <div className="text-center py-12">
                  <Image className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No active banners found</p>
                  <p className="text-sm text-gray-500 mt-2">Create banners in the Banner Management section</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-400 mb-4">
                    The sidebar shows the <strong className="text-white">first active banner</strong> from your banner list.
                    <br />
                    <span className="text-amber-500">Visible to: {settings.sidebar_banner.visible_tiers.map(t => TIERS.find(tier => tier.value === t)?.label).join(', ')}</span>
                  </p>
                  
                  {/* Sidebar Banner Preview - simulated sidebar width */}
                  <div className="mx-auto" style={{ maxWidth: '280px' }}>
                    <div className="rounded-xl overflow-hidden bg-black/20 border border-amber-500/30">
                      {(() => {
                        const firstActiveBanner = banners.find(b => b.is_active);
                        if (!firstActiveBanner) return null;
                        
                        return (
                          <div className="relative">
                            {firstActiveBanner.image_url ? (
                              <img 
                                src={firstActiveBanner.image_url} 
                                alt={firstActiveBanner.title || 'Sidebar Banner'} 
                                className="w-full h-auto object-cover"
                              />
                            ) : (
                              <div className="w-full h-32 bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                                <span className="text-white font-bold">{firstActiveBanner.title || 'Banner'}</span>
                              </div>
                            )}
                            {firstActiveBanner.link_url && (
                              <div className="absolute top-2 right-2">
                                <ExternalLink className="w-4 h-4 text-white/70" />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Simulated sidebar width (280px)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardDisplaySettings;
